import {Radio, Select} from "antd"
import {BaseTimePicker} from "components/DateTimePicker"
import {BaseInput, Icon} from "components"
import {getDisabledEndHours, getDisabledEndMinutes} from "helpers/calendar"
import {useModel} from "hooks"
import {Auth} from "types/auth"
import {BreakEventType, BreakTimeFieldErrors, BreakTimeForm, BreakTimeMode, BreakTimeType} from "types/work-schedule"
import styles from "./BreakTimeItem.module.css"

type Props = {
    item: BreakTimeForm
    disabled?: boolean
    fieldErrors?: BreakTimeFieldErrors
    onChange: (item: BreakTimeForm) => void
    onRemove: () => void
}

const BREAK_TIME_TYPE_OPTIONS = [
    {label: "AM", value: "am"},
    {label: "PM", value: "pm"},
    {label: "Fixed", value: "fixed"}
]

export function BreakTimeItem(props: Props) {
    const {item, onChange, onRemove, disabled, fieldErrors} = props
    const model = useModel()
    const timeFormat = model.user.timeFormat === Auth.TimeFormat.Time_12 ? "hh:mm A" : "HH:mm"

    const onChangeField = <K extends keyof BreakTimeForm>(field: K, value: BreakTimeForm[K]) => {
        onChange({...item, [field]: value})
    }

    const onChangeMode = (mode: BreakTimeMode) => {
        if (mode === "open") {
            onChange({
                ...item,
                mode,
                type: null,
                fromTime: undefined,
                toTime: undefined
            })
            return
        }

        onChange({
            ...item,
            mode,
            type: item.type ?? "am",
            fromTime: undefined,
            toTime: undefined
        })
    }

    const onChangeType = (type: BreakTimeType) => {
        onChange({
            ...item,
            type,
            fromTime: type === "fixed" ? item.fromTime : undefined,
            toTime: type === "fixed" ? item.toTime : undefined
        })
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Radio.Group value={item.mode} onChange={(e) => onChangeMode(e.target.value)}>
                    <Radio value="open">Open</Radio>
                    <Radio value="set">Set</Radio>
                </Radio.Group>
                {!disabled ? (
                    <div className={styles.removeBtn} onClick={onRemove}>
                        <Icon icon="DELETE" className={styles.deleteIcon} color="var(--error-400-base)" />
                    </div>
                ) : null}
            </div>

            <div className={styles.fieldRow}>
                <Radio.Group
                    value={item.breakEventType || BreakEventType.Break}
                    onChange={(e) => onChangeField("breakEventType", e.target.value)}>
                    <Radio value={BreakEventType.Break}>Break</Radio>
                    <Radio value={BreakEventType.Lunch}>Lunch</Radio>
                </Radio.Group>
            </div>

            {item.mode === "open" ? (
                <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>
                        Max Minutes <span className={styles.required}>*</span>
                    </span>
                    <BaseInput
                        min={0}
                        type="number"
                        value={item.maxMinutes}
                        onChange={(value) => onChangeField("maxMinutes", value)}
                        placeholder="Max Minutes"
                        error={!!fieldErrors?.maxMinutes}
                    />
                    {fieldErrors?.maxMinutes ? <span className={styles.errorText}>Max Minutes is required</span> : null}
                </div>
            ) : (
                <>
                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Type</span>
                        <Select
                            value={item.type ?? "am"}
                            onChange={onChangeType}
                            options={BREAK_TIME_TYPE_OPTIONS}
                            className={styles.typeSelect}
                            size="small"
                        />
                    </div>
                    {item.type === "fixed" ? (
                        <div className={styles.fieldRow}>
                            <div className={styles.timeRange}>
                                <div className={styles.timeField}>
                                    <span className={styles.fieldLabel}>
                                        From <span className={styles.required}>*</span>
                                    </span>
                                    <BaseTimePicker
                                        value={item.fromTime || undefined}
                                        onChange={(newValue) => onChangeField("fromTime", newValue)}
                                        placeholder="From"
                                        format={timeFormat}
                                        error={!!fieldErrors?.fromTime}
                                        suffixIcon={
                                            <div className={styles.calendarIconWrap}>
                                                <Icon icon="TIME_CLOCK_OUTLINE" color="#fff" />
                                            </div>
                                        }
                                        className={styles.timePicker}
                                        size="small"
                                    />
                                    {fieldErrors?.fromTime ? (
                                        <span className={styles.errorText}>From is required</span>
                                    ) : null}
                                </div>
                                <div className={styles.timeField}>
                                    <span className={styles.fieldLabel}>
                                        To <span className={styles.required}>*</span>
                                    </span>
                                    <BaseTimePicker
                                        value={item.toTime || undefined}
                                        onChange={(newValue) => onChangeField("toTime", newValue)}
                                        disabledHours={() => getDisabledEndHours(item.fromTime)}
                                        disabledMinutes={() => getDisabledEndMinutes(item.fromTime, item.toTime)}
                                        placeholder="To"
                                        format={timeFormat}
                                        error={!!fieldErrors?.toTime}
                                        suffixIcon={
                                            <div className={styles.calendarIconWrap}>
                                                <Icon icon="TIME_CLOCK_OUTLINE" color="#fff" />
                                            </div>
                                        }
                                        className={styles.timePicker}
                                        size="small"
                                    />
                                    {fieldErrors?.toTime ? (
                                        <span className={styles.errorText}>To is required</span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>
                            Max Minutes <span className={styles.required}>*</span>
                        </span>
                        <BaseInput
                            min={0}
                            type="number"
                            value={item.maxMinutes}
                            onChange={(value) => onChangeField("maxMinutes", value)}
                            placeholder="Max Minutes"
                            error={!!fieldErrors?.maxMinutes}
                        />
                        {fieldErrors?.maxMinutes ? (
                            <span className={styles.errorText}>Max Minutes is required</span>
                        ) : null}
                    </div>
                </>
            )}
        </div>
    )
}
